import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { publicApiClient } from "../../../api/apiClient";
import "./PartnershipDetail.css";

const FILE_BASE_URL = "https://api.dswustudent.kr";

const DURATION_LABEL = {
  LONG: "장기",
  SHORT: "단기",
};

const CATEGORY_LABEL = {
  FOOD: "식음료",
  CULTURE: "문화여가",
  LIFE: "생활편의",
};

const toAbsoluteUrl = (url) => {
  if (!url) return "";

  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }

  if (url.startsWith("/")) {
    return `${FILE_BASE_URL}${url}`;
  }

  return `${FILE_BASE_URL}/${url}`;
};

const formatDate = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10).replaceAll("-", ".");
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}.${mm}.${dd}`;
};

const formatPeriod = (startDate, endDate) => {
  const start = formatDate(startDate);
  const end = formatDate(endDate);

  if (!start && !end) return "-";
  if (start && !end) return start;
  if (!start && end) return end;
  if (start === end) return start;

  return `${start}~${end}`;
};

const getTimeValue = (value) => {
  if (!value) return 0;

  const date = new Date(value);
  const time = date.getTime();

  return Number.isNaN(time) ? 0 : time;
};

const getListFromResponse = (result) => {
  const data = result?.data || result;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.content)) return data.data.content;

  return [];
};

const getImages = (item) => {
  const result = [];

  const mainImg = toAbsoluteUrl(item.mainImg);

  if (mainImg) {
    result.push(mainImg);
  }

  const detailImages = (item.images || [])
    .map((image) => {
      if (typeof image === "string") {
        return toAbsoluteUrl(image);
      }

      return toAbsoluteUrl(image.url || image.imageUrl || image.fileUrl);
    })
    .filter(Boolean);

  detailImages.forEach((url) => {
    if (!result.includes(url)) {
      result.push(url);
    }
  });

  return result;
};

const normalizePartner = (item) => {
  const id = item.partnershipId || item.id;
  const durationType = item.durationType || "LONG";
  const category = item.category || "LIFE";

  return {
    id,
    title: item.title || item.companyName || "",
    companyName: item.companyName || "",
    termType: DURATION_LABEL[durationType] || durationType,
    categoryLabel: CATEGORY_LABEL[category] || category,
    writer: item.createdBy || "관리자",
    date: formatDate(item.createdAt),
    period: formatPeriod(item.startDate, item.endDate),
    content: item.content || "",
    createdAtRaw: item.createdAt || "",
    images: getImages(item),
  };
};

export default function PartnershipDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [item, setItem] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDetail = async () => {
    try {
      setLoading(true);

      const result = await publicApiClient(`/api/activity/partnership/${id}`, {
        method: "GET",
      });

      console.log("제휴 상세 조회 성공:", result);

      const data = result?.data || result;

      setItem(normalizePartner(data));
    } catch (error) {
      console.error("제휴 상세 조회 실패:", error);
      setItem(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchPartners = async () => {
    try {
      const result = await publicApiClient(
        "/api/activity/partnership?listCategory=ALL&page=0&size=100&sort=createdAt,desc",
        {
          method: "GET",
        }
      );

      const list = getListFromResponse(result)
        .map(normalizePartner)
        .filter((partner) => partner.id)
        .sort((a, b) => {
          const timeA = getTimeValue(a.createdAtRaw);
          const timeB = getTimeValue(b.createdAtRaw);

          if (timeA !== timeB) {
            return timeB - timeA;
          }

          return Number(b.id || 0) - Number(a.id || 0);
        });

      setPartners(list);
    } catch (error) {
      console.error("제휴 이전/다음 목록 조회 실패:", error);
      setPartners([]);
    }
  };

  useEffect(() => {
    fetchDetail();
    fetchPartners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const { prev, next } = useMemo(() => {
    const currentIndex = partners.findIndex(
      (partner) => Number(partner.id) === Number(id)
    );

    if (currentIndex === -1) {
      return {
        prev: null,
        next: null,
      };
    }

    return {
      next: partners[currentIndex - 1] || null,
      prev: partners[currentIndex + 1] || null,
    };
  }, [partners, id]);

  if (loading) {
    return (
      <div className="partnership-detail-not-found">
        게시글을 불러오는 중입니다.
      </div>
    );
  }

  if (!item) {
    return (
      <div className="partnership-detail-not-found">
        게시글을 찾을 수 없습니다.
      </div>
    );
  }

  return (
    <div className="partnership-detail-wrap">
      <div className="partnership-detail-inner">
        <div className="partnership-detail-title">
          [{item.termType}] {item.title}
        </div>

        <div className="partnership-detail-meta-author">
          <span className="partnership-detail-author-name">{item.writer}</span>
        </div>

        <div className="partnership-detail-meta-row">
          <span>
            <span className="partnership-detail-label">작성일</span>{" "}
            {item.date || "-"}
          </span>
        </div>

        <div className="partnership-detail-meta-row">
          <span>
            <span className="partnership-detail-label">카테고리</span>{" "}
            {item.categoryLabel}
          </span>
        </div>

        <div className="partnership-detail-meta-row">
          <span>
            <span className="partnership-detail-label">제휴 기간</span>{" "}
            {item.period}
          </span>
        </div>

        <div className="partnership-detail-divider" />

        {item.content && (
          <div className="partnership-detail-body">
            <p>{item.content}</p>
          </div>
        )}

        {item.images.length > 0 && (
          <div className="partnership-detail-images">
            {item.images.map((img, i) => (
              <img
                key={`${img}-${i}`}
                src={img}
                alt={`${item.title} ${i + 1}`}
                className="partnership-detail-image"
              />
            ))}
          </div>
        )}

        <div className="partnership-detail-nav">
          <div
            className={`partnership-detail-nav-item ${!next ? "disabled" : ""}`}
            onClick={() =>
              next && navigate(`/stdcouncil-log/Partnership/${next.id}`)
            }
          >
            <span className="partnership-detail-nav-arrow">∧</span>
            <span className="partnership-detail-nav-text">
              {next ? `[${next.termType}] ${next.title}` : "다음 글이 없습니다"}
            </span>
          </div>

          <div
            className={`partnership-detail-nav-item ${!prev ? "disabled" : ""}`}
            onClick={() =>
              prev && navigate(`/stdcouncil-log/Partnership/${prev.id}`)
            }
          >
            <span className="partnership-detail-nav-arrow">∨</span>
            <span className="partnership-detail-nav-text">
              {prev ? `[${prev.termType}] ${prev.title}` : "이전 글이 없습니다"}
            </span>
          </div>
        </div>

        <div className="partnership-detail-back-row">
          <button
            className="partnership-detail-back-btn"
            onClick={() => navigate("/stdcouncil-log/Partnership")}
            type="button"
          >
            목록
          </button>
        </div>
      </div>
    </div>
  );
}